import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  useTheme
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getCookie } from "../../../components/layout/cookies/CookieFunctions";
import ModalLoadingMessage from "../../../components/modals/ModalLoadingMessage";
import { postNotification } from "../../../components/utilities/Notifications";
import {
  findGetParameter,
  postData
} from "../../../components/utilities/UtilityFunctions";
import { loadStripeAsync } from "./utils";

export default function SubscriptionIntentCheckout(props) {
  const { product, priceId, coupon, couponCode, gameStoreId } = props;

  const affiliate = findGetParameter("affiliate");
  const [subscription, setSubscription] = useState({
    stripe: {
      clientSecret: "",
      subscriptionId: ""
    }
  });

  const [stripePromise, setStripePromise] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  const theme = useTheme();

  useEffect(() => {
    const go = async () => {
      try {
        var body = {
          priceId: priceId,
          subscriptionType: product?.type
        };

        const lastEventId = getCookie("lastEventId");
        const lastListId = getCookie("lastListId");

        if (affiliate) {
          body.affiliate = affiliate;
        } else {
          var cookieAffiliate = getCookie("affiliate");
          if (cookieAffiliate) {
            body.affiliate = cookieAffiliate;
          }
        }
        if (coupon?.promotionCode) {
          body.promotionCode = coupon?.promotionCode;
        } else if (couponCode) {
          body.couponCode = couponCode;
        } else if (coupon?.code) {
          body.couponCode = coupon?.code;
        }
        if (lastEventId) {
          body.lastEventId = lastEventId;
        }
        if (lastListId) {
          body.lastListId = lastListId;
        }

        if (gameStoreId) {
          body.gameStoreId = gameStoreId;
        }

        let apiSubscription = await postData(
          `/stripe/create${
            gameStoreId && gameStoreId !== ""
              ? "StoreSubscription"
              : "Subscription"
          }`,
          {
            body: body
          }
        );

        if (
          gameStoreId &&
          gameStoreId !== "" &&
          apiSubscription &&
          !apiSubscription?.clientSecret &&
          !apiSubscription?.stripe?.clientSecret
        ) {
          window.location.assign(
            `${window.location.protocol}//${window.location.host}/organize/organization/${gameStoreId}?active_tab=subscriptions`
          );
        } else {
          setSubscription(apiSubscription);
        }
      } catch (err) {
        postNotification("error", err);
      }
    };

    go();
    // eslint-disable-next-line
  }, []);

  // Load Stripe only when we actually need to render payment elements
  useEffect(() => {
    console.log({ subscription, stripePromise, stripeLoading });
    if (
      subscription?.stripe?.clientSecret &&
      !stripePromise &&
      !stripeLoading
    ) {
      setStripeLoading(true);
      loadStripeAsync()
        .then((stripe) => {
          setStripePromise(stripe);
          setStripeLoading(false);
        })
        .catch((error) => {
          console.error("Failed to load Stripe:", error);
          setStripeLoading(false);
        });
    }
    // eslint-disable-next-line
  }, [subscription?.stripe?.clientSecret, stripePromise, stripeLoading]);

  const appearance = {
    theme: theme?.palette?.mode === "dark" ? "night" : "stripe"
  };
  const options = {
    clientSecret: subscription?.stripe?.clientSecret,
    appearance
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Container
        sx={{
          pt: 2,
          pb: 2
        }}
        maxWidth="lg"
      >
        {subscription?.stripe?.clientSecret && stripePromise ? (
          <DynamicStripeElements options={options} stripe={stripePromise}>
            <DynamicCheckoutForm subData={subscription} />
          </DynamicStripeElements>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              maxWidth: "100%",
              minWidth: "100%",
              minHeight: "300px"
            }}
          >
            {(stripeLoading || !subscription?.stripe?.clientSecret) && (
              <CircularProgress size={100} />
            )}
          </Box>
        )}
      </Container>
    </Box>
  );
}

function DynamicStripeElements({ children, ...props }) {
  const [ElementsComponent, setElementsComponent] = useState(null);

  useEffect(() => {
    const loadElements = async () => {
      try {
        const { Elements } = await import("@stripe/react-stripe-js");
        setElementsComponent(() => Elements);
      } catch (error) {
        console.error("Failed to load Stripe Elements:", error);
      }
    };
    loadElements();
  }, []);

  if (!ElementsComponent) {
    return <CircularProgress />;
  }

  return React.createElement(ElementsComponent, props, children);
}

function ActualCheckoutForm({
  subData,
  StripeComponents,
  isLoading,
  setIsLoading,
  message,
  setMessage
}) {
  const stripe = StripeComponents.useStripe();
  const elements = StripeComponents.useElements();

  const { t } = useTranslation("translation", {
    keyPrefix: "components.playerRegistration"
  });

  useEffect(() => {
    if (!stripe) {
      return;
    }

    const clientSecret = new URLSearchParams(window.location.search).get(
      "payment_intent_client_secret"
    );

    if (!clientSecret) {
      return;
    }

    stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
      switch (paymentIntent.status) {
        case "succeeded":
          setMessage("Payment succeeded!");
          break;
        case "processing":
          setMessage("Your payment is processing.");
          break;
        case "requires_payment_method":
          setMessage("Your payment was not successful, please try again.");
          break;
        default:
          setMessage("Something went wrong.");
          break;
      }
    });
  }, [stripe, setMessage]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.protocol}//${window.location.host}/subscription/${subData.id}`
        }
      });

      if (error) {
        if (error.type === "card_error" || error.type === "validation_error") {
          setMessage(error.message);
        } else {
          setMessage("An unexpected error occured.");
        }
      }
    } catch (err) {
      console.error("Payment confirmation error:", err);
      setMessage("An unexpected error occured.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form id="payment-form" onSubmit={handleSubmit}>
      <Grid container spacing={1}>
        <Grid item xs={12} sx={{ minHeight: "230px" }}>
          <StripeComponents.PaymentElement id="payment-element" />
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={1} sx={{ mt: 2 }}>
            <Grid item xs={12}>
              <Button
                disabled={isLoading || !stripe || !elements}
                fullWidth
                id="submit"
                variant="contained"
                color="success"
                type="submit"
              >
                {t("payNow")}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {isLoading && <ModalLoadingMessage open={isLoading} message={message} />}
    </form>
  );
}

function DynamicCheckoutForm(props) {
  const { subData } = props;
  const [StripeComponents, setStripeComponents] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    const loadStripeComponents = async () => {
      try {
        const stripeReact = await import("@stripe/react-stripe-js");
        setStripeComponents({
          useStripe: stripeReact.useStripe,
          useElements: stripeReact.useElements,
          PaymentElement: stripeReact.PaymentElement
        });
      } catch (error) {
        console.error("Failed to load Stripe components:", error);
      }
    };
    loadStripeComponents();
  }, []);

  if (!StripeComponents) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "230px"
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ActualCheckoutForm
      subData={subData}
      StripeComponents={StripeComponents}
      isLoading={isLoading}
      setIsLoading={setIsLoading}
      message={message}
      setMessage={setMessage}
    />
  );
}
